import httpClient, { httpFormDataClient } from "./httpClient";

const prefix = "/holidays";

export interface HolidayParams {
  limit?: number;
  offset?: number;
  param?: string;
  sort?: string;
  order?: string;
  status?: string;
  holidayStartDate?: string;
  holidayEndDate?: string;
  branchId?: string;
  vacationType?: string;
}

export interface CreateHolidayDto {
  description?: string;
  approved?: boolean;
  isRejected?: boolean;
  isRequest?: boolean;
  startDate?: number;
  endDate?: number;
  daysRequested?: number;
  reviewerComment?: string;
  curp?: string;
  idPartner?: number;
  idReviewerUser?: number;
}

export interface UpdateHolidayDto {
  description?: string;
  approved?: boolean;
  isRejected?: boolean;
  isRequest?: boolean;
  startDate?: number;
  endDate?: number;
  daysRequested?: number;
  reviewerComment?: string;
  curp?: string;
  idPartner?: number;
  idReviewerUser?: number;
}

export interface HolidayPeriodDto {
  startYear: number;
  endYear: number;
  isActive?: boolean;
  description?: string;
  idPartner?: number;
}

export interface HolidayBalanceParams {
  limit?: number;
  offset?: number;
  param?: string;
  sort?: string;
  order?: string;
  branchId?: string;
  vacationType?: string;
}

export interface HolidayBalanceDto {
  totalDays: number;
  daysTaken?: number;
  daysPending?: number;
  daysRemaining?: number;
  notes?: string;
  idPartner: number;
  idHolidayPeriod: number;
}

export async function createHoliday(partnerId, holidayData, holidayFiles) {
  const formData = new FormData();

  if (holidayFiles && holidayFiles.length > 0) {
    holidayFiles.forEach((file) => {
      formData.append("holidayFiles", file);
    });
  }

  Object.keys(holidayData).forEach((key) => {
    formData.append(key, holidayData[key]);
  });

  return (await httpFormDataClient.post(`${prefix}/${partnerId}`, formData))
    .data;
}

export async function deleteHolidayFile(id) {
  return (await httpClient.delete(`${prefix}/holiday-files/${id}`)).data;
}

export async function updateHoliday(id, updateData) {
  return (await httpClient.patch(`${prefix}/${id}`, updateData)).data;
}

export async function uploadFilesToHoliday(id, holidayFiles) {
  const formData = new FormData();

  if (holidayFiles && holidayFiles.length > 0) {
    holidayFiles.forEach((file) => {
      formData.append("holidayFiles", file);
    });
  }

  return (await httpFormDataClient.post(`${prefix}/${id}/files`, formData))
    .data;
}

export async function approveHoliday(id, approveData) {
  return (await httpClient.patch(`${prefix}/${id}/approve`, approveData)).data;
}

export async function getHolidaysByPartner(partnerId) {
  return (await httpClient.get(`${prefix}/partner/${partnerId}`)).data;
}

export async function getHolidayById(id) {
  return (await httpClient.get(`${prefix}/${id}`)).data;
}

export async function getAllHolidays(params: HolidayParams = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.offset) queryParams.append('offset', String(params.offset));
  if (params.param) queryParams.append('param', params.param);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.order) queryParams.append('order', params.order);
  if (params.status) queryParams.append('status', params.status);
  if (params.holidayStartDate) queryParams.append('holidayStartDate', params.holidayStartDate);
  if (params.holidayEndDate) queryParams.append('holidayEndDate', params.holidayEndDate);
  if (params.branchId && params.branchId !== 'all') queryParams.append('branchId', params.branchId);
  if (params.vacationType && params.vacationType !== 'all') queryParams.append('vacationType', params.vacationType);

  // @ts-ignore
  if (params.bossId) {
    // @ts-ignore
    const bossId = params.bossId;
    const url = queryParams.toString() ? `${prefix}/by-direct-boss/${bossId}?${queryParams.toString()}` : `${prefix}/by-direct-boss/${bossId}`;
    return (await httpClient.get(url)).data;
  }

  const url = queryParams.toString() ? `${prefix}?${queryParams.toString()}` : prefix;
  return (await httpClient.get(url)).data;
}

export async function getAllHolidaysWithoutPagination() {
  return (await httpClient.get(`${prefix}/all`)).data;
}

export async function getTodayHolidaysByPartner(partnerId) {
  return (await httpClient.get(`${prefix}/today/${partnerId}`)).data;
}

export async function hasActiveHoliday(partnerId) {
  return (await httpClient.get(`${prefix}/partner/${partnerId}/active`)).data;
}

export async function getActiveHolidayByPartner(partnerId, date) {
  return (await httpClient.get(`${prefix}/partner/${partnerId}/active/${date}`)).data;
}

export async function hasPendingHoliday(partnerId) {
  return (await httpClient.get(`${prefix}/pending/${partnerId}`)).data;
}

export async function hasActiveHolidayForDate(partnerId, date) {
  return (await httpClient.get(`${prefix}/active/${partnerId}/${date}`)).data;
}

// Holiday Period Services
export async function createHolidayPeriod(data) {
  return (await httpClient.post(`${prefix}/periods`, data)).data;
}

export async function getAllHolidayPeriods() {
  return (await httpClient.get(`${prefix}/periods/all`)).data;
}

// Holiday Balance Services
export async function createHolidayBalance(data) {
  return (await httpClient.post(`${prefix}/balance`, data)).data;
}

export async function getBalanceByPartner(partnerId) {
  return (await httpClient.get(`${prefix}/balance/partner/${partnerId}`)).data;
}

export async function exportHolidaysExcel(year?: number, onlyActive: boolean = true) {
  const params = new URLSearchParams();
  if (year) params.append('year', String(year));
  if (onlyActive) params.append('onlyActive', 'true');
  const url = params.toString() ? `${prefix}/export/excel?${params.toString()}` : `${prefix}/export/excel`;
  const response = await httpClient.get(url, { responseType: 'blob' });
  return response;
}

export async function getAllBalancesPaginated(params: HolidayBalanceParams & { bossId?: number } = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.offset) queryParams.append('offset', String(params.offset));
  if (params.param) queryParams.append('param', params.param);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.order) queryParams.append('order', params.order);
  if (params.branchId && params.branchId !== 'all') queryParams.append('branchId', params.branchId);
  if (params.vacationType && params.vacationType !== 'all') queryParams.append('vacationType', params.vacationType);

  let url = `${prefix}/balances`;
  if (params.bossId) {
    url = `${prefix}/balances/by-direct-boss/${params.bossId}`;
  }

  const finalUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
  return (await httpClient.get(finalUrl)).data;
}

export async function getBalancesTotals(params: HolidayBalanceParams & { bossId?: number } = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.offset) queryParams.append('offset', String(params.offset));
  if (params.param) queryParams.append('param', params.param);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.order) queryParams.append('order', params.order);
  if (params.branchId && params.branchId !== 'all') queryParams.append('branchId', params.branchId);
  if (params.vacationType && params.vacationType !== 'all') queryParams.append('vacationType', params.vacationType);

  let url = `${prefix}/balances/totals`;
  if (params.bossId) {
    url = `${prefix}/balances/by-direct-boss/${params.bossId}/totals`;
  }

  const finalUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
  return (await httpClient.get(finalUrl)).data;
}

export async function getPartnerBalanceDetails(partnerId: number) {
  return (await httpClient.get(`${prefix}/balances/partner/${partnerId}/details`)).data;
}

export async function importHolidaysFromExcel(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  
  return (await httpFormDataClient.post(`${prefix}/import/excel`, formData)).data;
}

export async function updateHolidayBalance(id: number, data: HolidayBalanceDto) {
  return (await httpClient.patch(`${prefix}/balance/${id}`, data)).data;
}

export async function deleteHolidayBalance(id: number) {
  return (await httpClient.delete(`${prefix}/balance/${id}`)).data;
}
